import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Camera, Check, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AuthImg } from '@/components/construction/AuthImg';
import { tryGetLocation } from '@/lib/construction-offline';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface FixedCostEntry {
  id: string;
  fixedCostId: string;
  periodMonth: string;
  amount: number | string;
  receiptUrl: string | null;
}

interface FixedCost {
  id: string;
  costType: string;
  label: string;
  defaultAmount: number | string;
  entries: FixedCostEntry[];
}

interface RowState {
  fixedCostId: string;
  amount: string;
  photoFile: File | null;
  photoPreview: string | null;
  /** existing entry for the selected period, if any */
  existing: FixedCostEntry | null;
  /** whether the user has changed amount or attached a new photo */
  dirty: boolean;
}

const fmtSAR = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const currentMonth = () => {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
};
const fmtMonthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

export default function FixedCostsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [periodMonth, setPeriodMonth] = useState(currentMonth());
  const [costs, setCosts] = useState<FixedCost[]>([]);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    if (user && !isAdmin) navigate('/dashboard/construction', { replace: true });
  }, [user, isAdmin, navigate]);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await apiClient.get<FixedCost[]>(`/api/v1/construction/projects/${projectId}/fixed-costs`);
      setCosts(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Reset row state when costs or month changes
  useEffect(() => {
    const next: Record<string, RowState> = {};
    for (const fc of costs) {
      const existing = fc.entries.find((e) => {
        const ym = e.periodMonth.slice(0, 7);
        return ym === periodMonth;
      }) ?? null;
      next[fc.id] = {
        fixedCostId: fc.id,
        amount: existing ? String(Number(existing.amount)) : String(Number(fc.defaultAmount) || ''),
        photoFile: null,
        photoPreview: null,
        existing,
        dirty: false,
      };
    }
    setRows(next);
  }, [costs, periodMonth]);

  function setAmount(id: string, v: string) {
    setRows((r) => ({ ...r, [id]: { ...r[id], amount: v, dirty: true } }));
  }

  function pickPhoto(id: string, f: File) {
    setRows((r) => {
      const prev = r[id]?.photoPreview;
      if (prev) URL.revokeObjectURL(prev);
      return { ...r, [id]: { ...r[id], photoFile: f, photoPreview: URL.createObjectURL(f), dirty: true } };
    });
  }

  function clearPhoto(id: string) {
    setRows((r) => {
      const prev = r[id]?.photoPreview;
      if (prev) URL.revokeObjectURL(prev);
      return { ...r, [id]: { ...r[id], photoFile: null, photoPreview: null } };
    });
    const ref = fileRefs.current[id];
    if (ref) ref.value = '';
  }

  async function saveOne(row: RowState): Promise<void> {
    if (!projectId) return;
    const amt = Number(row.amount);
    if (!Number.isFinite(amt) || amt < 0) throw new Error('Invalid amount');
    if (!row.dirty) return;

    if (row.photoFile) {
      const fd = new FormData();
      fd.append('file', row.photoFile);
      fd.append('fixedCostId', row.fixedCostId);
      fd.append('periodMonth', periodMonth);
      fd.append('amount', String(amt));
      const gps = await tryGetLocation();
      if (gps) {
        fd.append('lat', String(gps.lat));
        fd.append('lng', String(gps.lng));
      }
      const jwt = getStoredToken();
      const res = await fetch(
        `${API_BASE}/api/v1/construction-photos/projects/${projectId}/fixed-cost`,
        { method: 'POST', headers: jwt ? { Authorization: `Bearer ${jwt}` } : {}, body: fd },
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Upload failed: ${txt || res.statusText}`);
      }
    } else {
      // JSON upsert (no photo)
      await apiClient.post(
        `/api/v1/construction/projects/${projectId}/fixed-cost-entries`,
        {
          fixedCostId: row.fixedCostId,
          periodMonth,
          amount: amt,
        },
      );
    }
  }

  async function saveAll() {
    setSaving(true);
    setError(null);
    try {
      const dirtyRows = Object.values(rows).filter((r) => r.dirty);
      for (const row of dirtyRows) {
        await saveOne(row);
      }
      await refresh();
      setSavedAt(Date.now());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const total = Object.values(rows).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const dirtyCount = Object.values(rows).filter((r) => r.dirty).length;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/construction">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </Link>
          <div className="text-sm font-medium">Fixed costs</div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div className="space-y-1">
            <Label htmlFor="fc-month">Month</Label>
            <Input
              id="fc-month"
              type="month"
              value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value || currentMonth())}
              className="w-44"
            />
          </div>
          <div className="text-right text-sm">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Total this month</div>
            <div className="font-mono text-lg">{fmtSAR(total)}</div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">{error}</div>
        )}

        {!loading && costs.length === 0 && !error && (
          <div className="text-center py-12 text-muted-foreground text-sm">No fixed costs configured for this project.</div>
        )}

        <div className="space-y-3">
          {costs.map((fc) => {
            const row = rows[fc.id];
            if (!row) return null;
            return (
              <div key={fc.id} className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-medium text-sm">{fc.label}</div>
                    <div className="text-xs text-muted-foreground">Default {fmtSAR(Number(fc.defaultAmount))}</div>
                  </div>
                  {row.existing && !row.dirty && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                      <Check className="h-3 w-3" /> Saved
                    </span>
                  )}
                  {row.dirty && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">Unsaved</span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor={`fc-amt-${fc.id}`}>Amount (SAR)</Label>
                    <Input
                      id={`fc-amt-${fc.id}`}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={row.amount}
                      onChange={(e) => setAmount(fc.id, e.target.value)}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-2">
                      Receipt <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <input
                      ref={(el) => { fileRefs.current[fc.id] = el; }}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) pickPhoto(fc.id, f);
                        e.target.value = '';
                      }}
                    />
                    {row.photoPreview ? (
                      <div className="flex items-center gap-2 rounded-md border border-border p-2">
                        <img src={row.photoPreview} alt="" className="h-12 w-12 rounded object-cover bg-muted flex-shrink-0" />
                        <div className="flex-1 text-xs text-emerald-700 dark:text-emerald-400 truncate">Ready</div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => clearPhoto(fc.id)} aria-label="Remove">
                          <XIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : row.existing?.receiptUrl ? (
                      <div className="flex items-center gap-2 rounded-md border border-border p-2">
                        <AuthImg src={row.existing.receiptUrl} alt="" className="h-12 w-12 rounded object-cover bg-muted flex-shrink-0" />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs gap-1"
                          onClick={() => fileRefs.current[fc.id]?.click()}
                        >
                          <Camera className="h-3.5 w-3.5" /> Replace
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-2 border-dashed h-11"
                        onClick={() => fileRefs.current[fc.id]?.click()}
                      >
                        <Camera className="h-4 w-4" />
                        Add receipt
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {costs.length > 0 && (
          <div className="sticky bottom-4 pt-2">
            <Button
              onClick={saveAll}
              disabled={saving || dirtyCount === 0}
              className="w-full gap-2 h-12"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {dirtyCount === 0 ? 'No changes to save' : `Save for ${fmtMonthLabel(periodMonth)} (${dirtyCount})`}
            </Button>
            {savedAt && Date.now() - savedAt < 4000 && (
              <div className="mt-2 text-center text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1 justify-center">
                <Check className="h-3 w-3" /> Saved
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
